import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { determineChipFeedEventDisplay } from '@gameday/components/Play/play/utils/displayHelpers';
import SingleAdDataContainer from '../Ads/containers/SingleAdDataContainer';
import usePlayTimer from '@gameday/utils/hooks/usePlayTimer';

import {
    ChipContainer,
    ChipWrapper,
    ChipWrapperFlex
} from './ChipFeed.style';

import { withTheme } from 'styled-components';
import SummaryPlayEvents from '../Play/play/SummaryPlayEvents';
import PlayPitches from '../Play/play/PlayPitches';
import moundVisitHelper from '@gameday/utils/moundVisitHelper';
import removePunctuation from '@gameday/utils/removePunctuation';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
const TOP = 'top';

const findLatestPlayEvent = (arr) => {
    for (let i = arr.length - 1; i > -1; i--) {
        const playEvent = arr[i];
        if (playEvent.endTime) {
            return playEvent;
        }
    }
    return null;
};

const displayPlayEvent = (playEvent, aboutPlay, isLoading, language, deviceInteraction, outcome, timer, ChipWrapperComponents = {}) => {
    const {
        details = {},
        isPitch,
        count: eventCount,
        pitchNumber,
        pitchData,
        playId
    } = playEvent;

    // Display a pitch in the chip
    if (isPitch) {
        const {
            ballColor,
            description,
            type,
            isBall,
            isStrike,
            isInPlay,
            isInPlayOuts,
            event
        } = details;
        const {
            startSpeed
        } = pitchData;
        const {
            balls,
            strikes
        } = eventCount;
        const { ChipWrapperComponent, ChipWrapperFlexComponent } = ChipWrapperComponents;
        const hasTitle = chipTitleCheck(event, description);
        const Wrapper = hasTitle ? ChipWrapperComponent || ChipWrapper : ChipWrapperFlexComponent || ChipWrapperFlex;

        return <Wrapper key={playId} $timer={timer}>
            <PlayPitches
                offset={ true }
                displayCount={ false }
                isLoading={ false }
                pitchNumber={ pitchNumber }
                ballColor={ ballColor }
                description={ outcome ? description + ` - ${outcome}` : description }
                pitchType={ type?.description }
                speed={ startSpeed }
                balls={ balls }
                strikes={ strikes }
                isBall={ isBall }
                isStrike={ isStrike }
                isInPlay={ isInPlay }
                isInPlayOuts={ isInPlayOuts }
            />
        </Wrapper>;
    // Display an event in the chip
    } else if (determineChipFeedEventDisplay(playEvent)) {
        const {
            event: e,
            eventType,
            description = '',
            homeScore,
            awayScore,
            isInPlay = false
        } = details;

        const isPlayEventOut = isInPlay && description.indexOf(', out') > 0;
        const hasTitle = chipTitleCheck(e, description);
        const Wrapper = hasTitle ? ChipWrapper : ChipWrapperFlex;

        return <Wrapper key={playId} $timer={timer}><SummaryPlayEvents
            styleType={'text'}
            event={ e }
            language={ language }
            description={ description }
            outs={ isPlayEventOut ? eventCount?.outs : undefined }
            homeScore={ homeScore }
            awayScore={ awayScore }
            isTopInning={ aboutPlay.halfInning === TOP }
            eventType={ eventType }
            isLoading={ isLoading }
            deviceInteraction={ deviceInteraction }
            hideIcon
        /></Wrapper>;
    }
    return null;
};

const displayPlayResult = ({ result, isLoading, language, currentPlay, timer, ChipWrapperComponents = {}, timerNum }) => {
    const {
        event,
        description,
        eventType
    } = result;

    if (event || description) {
        const { atBatIndex, playEvents = [] } = currentPlay;
        const totalPlaysEvents = playEvents?.length;
        const hasTitle = chipTitleCheck(event, description);
        const { ChipWrapperComponent, ChipWrapperFlexComponent } = ChipWrapperComponents;
        const Wrapper = hasTitle ? ChipWrapperComponent || ChipWrapper : ChipWrapperFlexComponent || ChipWrapperFlex;
        const showAd = moundVisitHelper(eventType, description, event);
        const keyId = atBatIndex + totalPlaysEvents;

        return (
            <>
                <Wrapper key={'r-' + keyId} $timer={timer} $timerNum={timerNum}>
                    <SummaryPlayEvents
                        styleType={'text'}
                        isResult
                        language={ language }
                        event={ event }
                        description={ description }
                        isLoading={ isLoading }
                        eventType={eventType}
                        hideIcon />
                </Wrapper>
                { showAd && <SingleAdDataContainer key={'presby-ad-' + keyId} type={'presby'} className={'chipfeed-ad'} />}
            </>
        );
    }
    return null;
};

const chipTitleCheck = (event, description) => {
    return event !== undefined && removePunctuation(event) !== removePunctuation(description);
};

const EMPTY_OBJECT = Object.freeze({});
const challengeTranslation = (reviewDetails, translate) => {
    const challengeKey = reviewDetails?.inProgress ? 'in_progress' : reviewDetails?.isOverturned ? 'overturned' : 'upheld';
    const challengeBackup = reviewDetails?.inProgress ? 'In Progress' : reviewDetails?.isOverturned ? 'Overturned' : 'Upheld';
    return translate(`abs_challenge_${challengeKey}`, challengeBackup);
};

const ChipFeed = ({
    currentPlay = {},
    isLoading = false,
    language = 'en',
    deviceInteraction,
    showAbsPitch
}) => {
    const {
        atBatIndex = 0,
        playEvents = [],
        result = {}
    } = currentPlay;

    const [chipContent, setChipContent] = useState(null);
    const currentPlayIndex = useRef(atBatIndex);
    const currentPlayEventLength = useRef(playEvents.length);
    const currentPlayResult = useRef(result.event || null);
    const playerTimerState = usePlayTimer() || EMPTY_OBJECT;
    const { timer } = playerTimerState;
    const translate = useTranslate();

    useEffect(() => {
        const {
            atBatIndex = 0,
            playEvents = [],
            result = null,
            about = {}
        } = currentPlay;
        const currentPlayIndexVal = currentPlayIndex?.current;
        const isNextPlay = atBatIndex > currentPlayIndexVal;
        const isNewerPlayEvent = atBatIndex === currentPlayIndexVal && playEvents.length > currentPlayEventLength?.current;
        const hasNewResult = !!(atBatIndex === currentPlayIndexVal && !currentPlayResult?.current && result?.event);
        let displayContent;

        // If we're now looking at the next play, display the latest item
        if (isNextPlay && playEvents.length > 0) {
            if (result?.event) {
                displayContent = displayPlayResult({ result, isLoading, language, currentPlay, timer });
            } else {
                const playEvent = findLatestPlayEvent(playEvents);
                if (playEvent) {
                    const { reviewDetails } = playEvent;
                    let outcome = '';
                    if (reviewDetails && showAbsPitch) {
                        outcome = challengeTranslation(reviewDetails, translate);
                    }
                    displayContent = displayPlayEvent(playEvent, {}, isLoading, language, deviceInteraction, outcome, timer);
                }
            }
        // If there's a new result display it
        } else if (hasNewResult) {
            displayContent = displayPlayResult({ result, isLoading, language, currentPlay, timer });
            // else, I GUESS we'll show an event...
        } else if (isNewerPlayEvent) {
            const newPlayEvents = playEvents.slice(currentPlayEventLength?.current, playEvents.length);
            const playEvent = findLatestPlayEvent(newPlayEvents);
            if (playEvent) {
                const { reviewDetails } = playEvent;
                let outcome = '';
                if (reviewDetails && showAbsPitch) {
                    outcome = challengeTranslation(reviewDetails, translate);
                }
                displayContent = displayPlayEvent(playEvent, about, isLoading, language, deviceInteraction, outcome, timer);
            }
        }

        // If we get old data, set the chip to null.
        if ((atBatIndex < currentPlayIndexVal || atBatIndex === currentPlayIndexVal) && playEvents.length < currentPlayEventLength?.current) {
            setChipContent(null);
        }

        // Set the chip content, even if there isn't anything to display.
        // This helps reset the chip at the beginning of new plays that
        // don't have any play events yet.
        setChipContent(displayContent);
        currentPlayIndex.current = atBatIndex;
        currentPlayEventLength.current = playEvents.length;
        currentPlayResult.current = result?.event;
    }, [currentPlay]);

    return <ChipContainer $timer={timer}>{chipContent}</ChipContainer>;
};

ChipFeed.propTypes = {
    currentPlay: PropTypes.object,
    isLoading: PropTypes.bool,
    language: PropTypes.string,
    deviceInteraction: PropTypes.object,
    showAbsPitch: PropTypes.bool
};

export default withTheme(ChipFeed);
